import { Fragment } from "react";
import { motion, useReducedMotion, type Variants } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1] as const;

interface SplitTextProps {
  text: string;
  /** délai entre chaque lettre (s) */
  stagger?: number;
  /** délai initial (s) */
  delay?: number;
  style?: React.CSSProperties;
  /** déclenche au scroll (true) ou au montage (false) */
  onView?: boolean;
}

// Révèle un texte lettre par lettre (fondu + montée + flou).
// Les lettres sont REGROUPÉES PAR MOT (chaque mot est insécable) : sur mobile,
// le retour à la ligne se fait uniquement entre les mots, jamais au milieu.
export default function SplitText({
  text,
  stagger = 0.04,
  delay = 0,
  style,
  onView = true,
}: SplitTextProps) {
  const reduce = useReducedMotion();
  if (reduce) return <span style={style}>{text}</span>;

  const container: Variants = { hidden: {}, visible: {} };
  const letter: Variants = {
    hidden: { opacity: 0, y: "0.55em", filter: "blur(6px)" },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.6, ease: EASE, delay: delay + i * stagger },
    }),
  };

  const words = text.split(" ");
  let index = 0;

  const animateProps = onView
    ? { whileInView: "visible" as const, viewport: { once: true, amount: 0.4 } }
    : { animate: "visible" as const };

  return (
    <motion.span
      variants={container}
      initial="hidden"
      {...animateProps}
      style={{ display: "inline", ...style }}
    >
      {words.map((word, wi) => (
        <Fragment key={wi}>
          {/* Mot insécable */}
          <span style={{ display: "inline-block", whiteSpace: "nowrap" }}>
            {Array.from(word).map((ch, ci) => {
              const i = index++;
              return (
                <motion.span
                  key={ci}
                  custom={i}
                  variants={letter}
                  style={{ display: "inline-block" }}
                >
                  {ch}
                </motion.span>
              );
            })}
          </span>
          {/* Espace sécable entre les mots */}
          {wi < words.length - 1 ? " " : null}
        </Fragment>
      ))}
    </motion.span>
  );
}
